// Dienstleisterliste (Handoff Dienstleister §6): Zeilen aus den Rohdaten, Filter, Zähler, Kennzahlen.
// Reine Funktionen – testbar.

import { normalisiereSuche } from '../immobilien/suche';
import { objektName } from '../immobilien/objektKopf';
import { aktuelleNachweise, NACHWEIS_LABEL, nachweisStatus, plusTage, type NachweisArt, type NachweisStatus } from './nachweise';
import { dlStatus, type DlStatus } from './status';

/** Vorwarnzeit für Vertragsenden in Liste und Widget „Läuft demnächst ab“. */
export const VERTRAG_WARNTAGE = 120;

export interface DlRoh {
  id: string;
  name: string;
  ansprechpartner: string | null;
  email: string | null;
  telefon: string | null;
  stadt: string | null;
  ist_notfallkontakt: boolean;
  ist_bevorzugt: boolean;
  beauftragung_modus: string | null;
  aktiv: boolean;
  created_at?: string | null;
  partner_seit?: string | null;
}
export interface KategorieRoh { id: string; name: string; ist_handwerk: boolean; aktiv: boolean; sortierung?: number | null }
export interface DlKategorieRoh { dienstleister_id: string; kategorie_id: string }
export interface DlObjektRoh {
  dienstleister_id: string; objekt_id: string; von: string | null; bis: string | null; vertragsart: string | null; aktiv: boolean | null;
  kuendigung_zum?: string | null;
  kosten_jahr?: number | string | null;
  umlagefaehig?: boolean | null;
}
export interface ObjektRoh { id: string; bezeichnung?: string | null; titel?: string | null; objektnummer?: string | null; strasse?: string | null; hausnummer?: string | null; stadt?: string | null; ort?: string | null }
export interface NachweisZeileRoh { dienstleister_id: string; art: string; gueltig_bis: string | null }
export interface RechnungRoh { dienstleister_id: string; brutto: number | string | null; status: string | null; datum: string | null }
export interface TicketRoh { dienstleister_id: string; status: string | null }
export interface BewertungRoh { dienstleister_id: string; sterne: number }

export interface DlListenDaten {
  dienstleister: DlRoh[];
  kategorien: KategorieRoh[];
  dlKategorien: DlKategorieRoh[];
  dlObjekte: DlObjektRoh[];
  objekte: ObjektRoh[];
  nachweise: NachweisZeileRoh[];
  rechnungen: RechnungRoh[];
  tickets: TicketRoh[];
  bewertungen: BewertungRoh[];
}

export interface DlAblauf { art: 'nachweis' | 'vertrag'; datum: string; text: string; status: NachweisStatus | 'vertrag' }

export interface DlZeile {
  id: string;
  name: string;
  ansprechpartner: string;
  email: string;
  telefon: string;
  stadt: string;
  aktiv: boolean;
  notfall: boolean;
  bevorzugt: boolean;
  angebot: boolean;
  handwerk: boolean;
  kategorien: string[];
  kategorieIds: string[];
  objektIds: string[];
  objektNamen: string[];
  volumen: number;
  offen: number;
  /** Anzahl zugeordneter Rechnungen */
  rechnungen: number;
  offeneAuftraege: number;
  bewertung: number | null;
  nachweisAbgelaufen: boolean;
  nachweisLaeuftAb: boolean;
  /** befristete Nachweise: gültig / läuft ab / abgelaufen */
  nachweisZahlen: { gueltig: number; laeuftAb: number; abgelaufen: number };
  vertragEndet: string | null;
  ablaeufe: DlAblauf[];
  status: DlStatus;
  /** 12 Monate Rechnungen: ok = alle bezahlt, warn = offen, vor = keine Rechnung */
  pips: { monat: string; label: string; farbe: 'ok' | 'warn' | 'vor' }[];
  suchtext: string;
}

const txt = (v: string | null | undefined) => (v ?? '').trim();
const zahl = (v: number | string | null) => (v === null ? 0 : Number(v) || 0);
/** Offene Rechnung = alles außer „bezahlt“ (Entscheidung 14.10.2026). */
export const istOffeneRechnung = (status: string | null) => (status ?? '').toLowerCase() !== 'bezahlt';
const ERLEDIGT = new Set(['erledigt', 'geschlossen']);
export const istOffenesTicket = (status: string | null) => !ERLEDIGT.has((status ?? '').toLowerCase());
/** Vertrag = Zuordnung mit Vertragsart außer „Einzelauftrag“; leere Vertragsart ist nur eine Zuordnung. */
export const istVertrag = (vertragsart: string | null) => !!txt(vertragsart) && txt(vertragsart).toLowerCase() !== 'einzelauftrag';
/** Zuordnung läuft (aktiv und nicht beendet). */
export const zuordnungLaeuft = (o: Pick<DlObjektRoh, 'aktiv' | 'bis'>, heute: string) => o.aktiv !== false && (!o.bis || o.bis.slice(0, 10) >= heute);

const MONAT = new Intl.DateTimeFormat('de-DE', { month: 'short', year: 'numeric' });

/** Die letzten 12 Monate (ältester zuerst) als „YYYY-MM“. */
export function letzteMonate(heute: string, n = 12): string[] {
  const j = Number(heute.slice(0, 4));
  const m = Number(heute.slice(5, 7)) - 1;
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(Date.UTC(j, m - (n - 1 - i), 1));
    return d.toISOString().slice(0, 7);
  });
}

function gruppiere<T extends { dienstleister_id: string }>(rows: readonly T[]): Map<string, T[]> {
  const je = new Map<string, T[]>();
  for (const r of rows) {
    const l = je.get(r.dienstleister_id);
    if (l) l.push(r); else je.set(r.dienstleister_id, [r]);
  }
  return je;
}

export function baueDlZeilen(d: DlListenDaten, heute: string): DlZeile[] {
  const kat = new Map(d.kategorien.map(k => [k.id, k]));
  const obj = new Map(d.objekte.map(o => [o.id, o]));
  const katJe = gruppiere(d.dlKategorien);
  const objJe = gruppiere(d.dlObjekte);
  const nwJe = gruppiere(d.nachweise);
  const reJe = gruppiere(d.rechnungen);
  const tiJe = gruppiere(d.tickets);
  const bwJe = gruppiere(d.bewertungen);
  const jahr = heute.slice(0, 4);
  const monate = letzteMonate(heute);
  const vertragGrenze = plusTage(heute, VERTRAG_WARNTAGE);

  return d.dienstleister.map(dl => {
    const kats = (katJe.get(dl.id) ?? []).map(x => kat.get(x.kategorie_id)).filter((k): k is KategorieRoh => !!k)
      .sort((a, b) => (a.sortierung ?? 0) - (b.sortierung ?? 0) || a.name.localeCompare(b.name, 'de'));
    const zuordnungen = (objJe.get(dl.id) ?? []).filter(o => zuordnungLaeuft(o, heute));
    const objektIds = [...new Set(zuordnungen.map(o => o.objekt_id))];
    const objektNamen = objektIds.map(id => obj.get(id)).filter((o): o is ObjektRoh => !!o).map(o => objektName(o));

    const nachweise = aktuelleNachweise(nwJe.get(dl.id) ?? []).filter(n => n.gueltig_bis);
    const nwStatus = nachweise.map(n => ({ n, st: nachweisStatus(n.gueltig_bis, heute) }));
    const nachweisZahlen = {
      gueltig: nwStatus.filter(x => x.st === 'gueltig').length,
      laeuftAb: nwStatus.filter(x => x.st === 'laeuft_ab').length,
      abgelaufen: nwStatus.filter(x => x.st === 'abgelaufen').length,
    };

    const vertragsEnden = zuordnungen.filter(o => istVertrag(o.vertragsart))
      .map(o => ({ o, ende: [o.bis, o.kuendigung_zum].filter((x): x is string => !!x).map(x => x.slice(0, 10)).sort()[0] ?? null }))
      .filter((x): x is { o: DlObjektRoh; ende: string } => !!x.ende && x.ende >= heute && x.ende <= vertragGrenze)
      .sort((a, b) => a.ende.localeCompare(b.ende));
    const vertragEndet = vertragsEnden[0]?.ende ?? null;

    const ablaeufe: DlAblauf[] = [
      ...nwStatus.filter(x => x.st !== 'gueltig').map(x => ({
        art: 'nachweis' as const, datum: x.n.gueltig_bis!.slice(0, 10), status: x.st,
        text: NACHWEIS_LABEL[x.n.art as NachweisArt] ?? x.n.art,
      })),
      ...vertragsEnden.map(x => {
        const o = obj.get(x.o.objekt_id);
        return { art: 'vertrag' as const, datum: x.ende, status: 'vertrag' as const, text: [txt(x.o.vertragsart), o ? objektName(o) : ''].filter(Boolean).join(' · ') };
      }),
    ];

    const rechnungen = reJe.get(dl.id) ?? [];
    const volumen = rechnungen.filter(r => (r.datum ?? '').startsWith(jahr)).reduce((s, r) => s + zahl(r.brutto), 0);
    const offen = rechnungen.filter(r => istOffeneRechnung(r.status)).reduce((s, r) => s + zahl(r.brutto), 0);
    const pips = monate.map(m => {
      const imMonat = rechnungen.filter(r => (r.datum ?? '').startsWith(m));
      const farbe: 'ok' | 'warn' | 'vor' = imMonat.length === 0 ? 'vor' : imMonat.some(r => istOffeneRechnung(r.status)) ? 'warn' : 'ok';
      const label = `${MONAT.format(new Date(`${m}-01T00:00:00Z`))}: ${imMonat.length === 0 ? 'keine Rechnung' : farbe === 'warn' ? 'Rechnung offen' : 'bezahlt'}`;
      return { monat: m, label, farbe };
    });

    const bw = bwJe.get(dl.id) ?? [];
    const bewertung = bw.length ? Math.round((bw.reduce((s, b) => s + b.sterne, 0) / bw.length) * 10) / 10 : null;
    const offeneAuftraege = (tiJe.get(dl.id) ?? []).filter(t => istOffenesTicket(t.status)).length;
    const nachweisAbgelaufen = nachweisZahlen.abgelaufen > 0;
    const nachweisLaeuftAb = nachweisZahlen.laeuftAb > 0;

    const z: DlZeile = {
      id: dl.id, name: txt(dl.name) || 'Ohne Namen', ansprechpartner: txt(dl.ansprechpartner), email: txt(dl.email), telefon: txt(dl.telefon), stadt: txt(dl.stadt),
      aktiv: dl.aktiv !== false, notfall: !!dl.ist_notfallkontakt, bevorzugt: !!dl.ist_bevorzugt, angebot: dl.beauftragung_modus === 'angebot',
      handwerk: kats.some(k => k.ist_handwerk), kategorien: kats.map(k => k.name), kategorieIds: kats.map(k => k.id),
      objektIds, objektNamen, volumen, offen, rechnungen: rechnungen.length, offeneAuftraege, bewertung, nachweisAbgelaufen, nachweisLaeuftAb, nachweisZahlen, vertragEndet,
      ablaeufe: ablaeufe.sort((a, b) => a.datum.localeCompare(b.datum)),
      status: dlStatus({ aktiv: dl.aktiv !== false, nachweisAbgelaufen, offenBetrag: offen, nachweisLaeuftAb, vertragEndet }),
      pips, suchtext: '',
    };
    z.suchtext = normalisiereSuche([z.name, z.ansprechpartner, z.email, z.stadt, ...z.kategorien, ...z.objektNamen].join(' '));
    return z;
  });
}

// ── Filter ─────────────────────────────────────────────────────────────────────

export type StatusFilter = 'aktiv' | 'rechnung' | 'ablauf' | 'inaktiv';
export const STATUS_FILTER: readonly { key: StatusFilter; label: string }[] = [
  { key: 'aktiv', label: 'Aktiv' }, { key: 'rechnung', label: 'Rechnung offen' }, { key: 'ablauf', label: 'Nachweis / Vertrag läuft ab' }, { key: 'inaktiv', label: 'Inaktiv' },
];
export type FlagFilter = 'alle' | 'notfall' | 'bevorzugt' | 'angebot';
export const FLAG_FILTER: readonly { key: FlagFilter; label: string }[] = [
  { key: 'alle', label: 'Alle' }, { key: 'notfall', label: 'Notfall 24/7' }, { key: 'bevorzugt', label: 'Bevorzugt' }, { key: 'angebot', label: 'Erst Angebot' },
];
export const istStatusFilter = (v: unknown): v is StatusFilter => STATUS_FILTER.some(f => f.key === v);
export const istFlagFilter = (v: unknown): v is FlagFilter => FLAG_FILTER.some(f => f.key === v);

export function passtStatus(z: DlZeile, f: StatusFilter): boolean {
  if (f === 'inaktiv') return !z.aktiv;
  if (!z.aktiv) return false;
  if (f === 'rechnung') return z.offen > 0.005;
  if (f === 'ablauf') return z.nachweisAbgelaufen || z.nachweisLaeuftAb || !!z.vertragEndet;
  return true;
}
export function passtFlag(z: DlZeile, f: FlagFilter): boolean {
  if (f === 'notfall') return z.notfall;
  if (f === 'bevorzugt') return z.bevorzugt;
  if (f === 'angebot') return z.angebot;
  return true;
}

/** Objekt- und Suchfilter (Basis für Zähler und Liste). */
export function basisZeilen(zeilen: readonly DlZeile[], objekt: string, suche: string): DlZeile[] {
  const q = normalisiereSuche(suche.trim());
  return zeilen.filter(z => (objekt === 'alle' || z.objektIds.includes(objekt)) && (!q || z.suchtext.includes(q)));
}

/** Zähler: Status-Segment richtet sich nach dem Flag, Flag-Segment zählt nur aktive (README §6). */
export function zaehler(basis: readonly DlZeile[], status: StatusFilter, flag: FlagFilter) {
  const st = {} as Record<StatusFilter, number>;
  for (const f of STATUS_FILTER) st[f.key] = basis.filter(z => passtStatus(z, f.key) && passtFlag(z, flag)).length;
  const fl = {} as Record<FlagFilter, number>;
  for (const f of FLAG_FILTER) fl[f.key] = basis.filter(z => passtFlag(z, f.key) && passtStatus(z, status === 'inaktiv' ? 'inaktiv' : 'aktiv')).length;
  return { status: st, flag: fl };
}

export function dlJeObjekt(zeilen: readonly DlZeile[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const z of zeilen) if (z.aktiv) for (const id of z.objektIds) m.set(id, (m.get(id) ?? 0) + 1);
  return m;
}

// ── Kennzahlen der Widgets ─────────────────────────────────────────────────────

export interface DlKennzahlen {
  aktiv: number; gewerke: number; offeneAuftraege: number; volumen: number;
  notfall: number; bevorzugt: number; angebot: number;
  offen: number; mitOffen: number;
  nachweisGueltig: number; nachweisLaeuftAb: number; nachweisQuote: number;
}

export function dlKennzahlen(zeilen: readonly DlZeile[]): DlKennzahlen {
  const aktiv = zeilen.filter(z => z.aktiv);
  const gueltig = aktiv.reduce((s, z) => s + z.nachweisZahlen.gueltig, 0);
  const ab = aktiv.reduce((s, z) => s + z.nachweisZahlen.laeuftAb + z.nachweisZahlen.abgelaufen, 0);
  return {
    aktiv: aktiv.length,
    gewerke: new Set(aktiv.flatMap(z => z.kategorieIds)).size,
    offeneAuftraege: aktiv.reduce((s, z) => s + z.offeneAuftraege, 0),
    volumen: aktiv.reduce((s, z) => s + z.volumen, 0),
    notfall: aktiv.filter(z => z.notfall).length,
    bevorzugt: aktiv.filter(z => z.bevorzugt).length,
    angebot: aktiv.filter(z => z.angebot).length,
    offen: zeilen.reduce((s, z) => s + z.offen, 0),
    mitOffen: zeilen.filter(z => z.offen > 0.005).length,
    nachweisGueltig: gueltig,
    nachweisLaeuftAb: ab,
    nachweisQuote: gueltig + ab === 0 ? 0 : Math.round((gueltig / (gueltig + ab)) * 100),
  };
}

/** Die nächsten Abläufe (Nachweise und Vertragsenden) über alle aktiven Dienstleister. */
export function naechsteAblaeufe(zeilen: readonly DlZeile[], n = 3): (DlAblauf & { id: string; name: string })[] {
  return zeilen.filter(z => z.aktiv).flatMap(z => z.ablaeufe.map(a => ({ ...a, id: z.id, name: z.name })))
    .sort((a, b) => a.datum.localeCompare(b.datum)).slice(0, n);
}

/** „Notfall 24/7 · bevorzugt · erst Angebot“ für die Kachel. */
export function flagText(z: Pick<DlZeile, 'notfall' | 'bevorzugt' | 'angebot'>): string {
  return [z.notfall ? 'Notfall 24/7' : '', z.bevorzugt ? 'bevorzugt' : '', z.angebot ? 'erst Angebot' : 'direkt beauftragen'].filter(Boolean).join(' · ');
}

/** Avatar: inaktiv grau, Notfall rot, Handwerk Akzent, sonst Blau (README §6). */
export function avatarTon(z: Pick<DlZeile, 'aktiv' | 'notfall' | 'handwerk'>): 'grau' | 'bad' | 'acc' | 'info' {
  return !z.aktiv ? 'grau' : z.notfall ? 'bad' : z.handwerk ? 'acc' : 'info';
}
